import { useEffect, useCallback } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
    $getSelection,
    $isRangeSelection,
    $nodesOfType,
    createCommand,
    COMMAND_PRIORITY_LOW,
} from 'lexical';
import { $wrapSelectionInMarkNode, MarkNode, $getMarkIDs } from '@lexical/mark';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveMarkId, openCommentsSidebar, setPendingMark } from '../../../redux/Slices/pageCommentsSlice';

// Custom command that the floating toolbar dispatches
export const INSERT_COMMENT_COMMAND = createCommand('INSERT_COMMENT_COMMAND');

function CommentsPlugin({ pageId, readOnly }) {
    const [editor] = useLexicalComposerContext();
    const dispatch = useDispatch();
    const activeMarkId = useSelector(state => state.pageComments.activeMarkId);

    // Sync data-mark-id attributes and active class on all mark DOM elements.
    // Runs on every editor update and whenever activeMarkId changes.
    useEffect(() => {
        const syncMarks = () => {
            const rootElement = editor.getRootElement();
            if (!rootElement) return;

            // Remove stale active classes
            rootElement.querySelectorAll('.editor-mark.active').forEach(el => {
                el.classList.remove('active');
            });

            editor.getEditorState().read(() => {
                const markNodes = $nodesOfType(MarkNode);
                for (const markNode of markNodes) {
                    const ids = markNode.getIDs();
                    const el = editor.getElementByKey(markNode.getKey());
                    if (el && ids.length > 0) {
                        // Tag the DOM element so floating cards can find it
                        el.setAttribute('data-mark-id', ids[0]);
                        if (activeMarkId && ids.includes(activeMarkId)) {
                            el.classList.add('active');
                        }
                    }
                }
            });
        };

        syncMarks();
        const unregister = editor.registerUpdateListener(syncMarks);
        return unregister;
    }, [editor, activeMarkId]);

    // Handle clicks on mark nodes to show the comment thread
    const handleMarkClick = useCallback((event) => {
        const target = event.target;
        if (!target || !(target instanceof HTMLElement)) return;

        // Check if clicked element or parent has editor-mark class
        const markElement = target.closest('.editor-mark');
        if (!markElement) return;

        // Read the editor state to find the mark IDs
        editor.getEditorState().read(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) return;

            const node = selection.anchor.getNode();
            const markIDs = $getMarkIDs(node, selection.anchor.offset);

            if (markIDs && markIDs.length > 0) {
                dispatch(setActiveMarkId(markIDs[0]));
            }
        });
    }, [editor, dispatch]);

    useEffect(() => {
        const rootElement = editor.getRootElement();
        if (!rootElement) return;

        rootElement.addEventListener('click', handleMarkClick);
        return () => {
            rootElement.removeEventListener('click', handleMarkClick);
        };
    }, [editor, handleMarkClick]);

    useEffect(() => {
        if (readOnly) return;

        return editor.registerCommand(
            INSERT_COMMENT_COMMAND,
            () => {
                const selection = $getSelection();
                if (!$isRangeSelection(selection) || selection.isCollapsed()) {
                    return false;
                }

                // Generate a unique mark ID
                const markId = `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

                // Get the selected text for the quoted snippet
                const selectedText = selection.getTextContent();

                // Wrap the selection in a mark node
                const isBackward = selection.isBackward();
                $wrapSelectionInMarkNode(selection, isBackward, markId);

                // Open the sidebar and set active mark
                dispatch(setPendingMark({ markId, quotedText: selectedText }));
                dispatch(setActiveMarkId(markId));
                dispatch(openCommentsSidebar());

                return true;
            },
            COMMAND_PRIORITY_LOW
        );
    }, [editor, dispatch, readOnly]);

    // Register keyboard shortcut
    useEffect(() => {
        if (readOnly) return;

        const handleKeyDown = (event) => {
            // Ctrl+Alt+M (or Cmd+Alt+M on Mac)
            if ((event.ctrlKey || event.metaKey) && event.altKey && event.key === 'm') {
                event.preventDefault();
                editor.dispatchCommand(INSERT_COMMENT_COMMAND, undefined);
            }
        };

        const rootElement = editor.getRootElement();
        if (rootElement) {
            rootElement.addEventListener('keydown', handleKeyDown);
            return () => rootElement.removeEventListener('keydown', handleKeyDown);
        }
    }, [editor, readOnly]);

    return null;
}

export default CommentsPlugin;
